import React, { useState, useRef, useEffect, useCallback } from 'react';

const JogWheel = ({ 
  deck, 
  isPlaying, 
  rotation = 0,
  albumArt,
  onScrub, // New: callback for scrubbing (delta in degrees)
  onScrubEnd, // New: callback when scrub ends
  position = 0, // Current position in seconds
  duration = 0, // Total duration in seconds
  size = 140
}) => {
  const deckColor = deck === 'A' ? '#00d4ff' : '#ff6b35';
  const deckColorDim = deck === 'A' ? 'rgba(0, 212, 255, 0.3)' : 'rgba(255, 107, 53, 0.3)';
  
  const [isDragging, setIsDragging] = useState(false);
  const [scratchRotation, setScratchRotation] = useState(0);
  const lastAngleRef = useRef(null);
  const wheelRef = useRef(null);
  
  // Calculate rotation based on position (for visual feedback when not dragging)
  const displayRotation = isDragging ? scratchRotation : rotation;
  
  const getAngleFromEvent = useCallback((e) => {
    if (!wheelRef.current) return 0;
    const rect = wheelRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
  }, []);
  
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setScratchRotation(rotation);
    lastAngleRef.current = getAngleFromEvent(e);
  }, [rotation, getAngleFromEvent]);
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      const currentAngle = getAngleFromEvent(e);
      if (lastAngleRef.current !== null) {
        let delta = currentAngle - lastAngleRef.current;
        
        // Handle wrap-around at ±180 degrees
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;
        
        setScratchRotation(prev => prev + delta);
        
        // Call scrub callback if provided
        if (onScrub && duration > 0) {
          // Convert rotation delta to time delta
          // More sensitive: 360 degrees = 5 seconds for precise control
          const timeDelta = (delta / 360) * 5;
          onScrub(timeDelta);
        }
      }
      lastAngleRef.current = currentAngle;
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      lastAngleRef.current = null;
      if (onScrubEnd) onScrubEnd();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, getAngleFromEvent, onScrub, onScrubEnd, duration]);
  
  return (
    <div 
      className={`jog-wheel-container ${deck.toLowerCase()} ${isDragging ? 'scratching' : ''}`}
      ref={wheelRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <svg 
        width={size} 
        height={size} 
        viewBox="0 0 200 200"
        className={`jog-wheel-svg ${isPlaying && !isDragging ? 'spinning' : ''}`}
        style={{ transform: `rotate(${displayRotation}deg)` }}
      >
        {/* Outer ring - metallic */}
        <defs>
          <radialGradient id={`metalGradient-${deck}`} cx="30%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#666" />
            <stop offset="50%" stopColor="#333" />
            <stop offset="100%" stopColor="#1a1a1a" />
          </radialGradient>
          <radialGradient id={`vinylGradient-${deck}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2a2a2a" />
            <stop offset="85%" stopColor="#1a1a1a" />
            <stop offset="100%" stopColor="#0a0a0a" />
          </radialGradient>
          <filter id={`glow-${deck}`}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Outer metallic ring */}
        <circle 
          cx="100" 
          cy="100" 
          r="98" 
          fill={`url(#metalGradient-${deck})`}
          stroke={isDragging ? deckColor : "#444"}
          strokeWidth={isDragging ? "3" : "2"}
        />
        
        {/* Rubber grip ring */}
        <circle 
          cx="100" 
          cy="100" 
          r="92" 
          fill="none"
          stroke="#222"
          strokeWidth="8"
        />
        
        {/* Grip dots */}
        {[...Array(36)].map((_, i) => {
          const angle = (i * 10) * Math.PI / 180;
          const x = 100 + 88 * Math.cos(angle);
          const y = 100 + 88 * Math.sin(angle);
          return (
            <circle 
              key={i}
              cx={x} 
              cy={y} 
              r="1.5" 
              fill="#444"
            />
          );
        })}
        
        {/* Vinyl surface */}
        <circle 
          cx="100" 
          cy="100" 
          r="82" 
          fill={`url(#vinylGradient-${deck})`}
        />
        
        {/* Vinyl grooves */}
        {[...Array(12)].map((_, i) => (
          <circle 
            key={i}
            cx="100" 
            cy="100" 
            r={75 - i * 5}
            fill="none"
            stroke="rgba(255,255,255,0.03)"
            strokeWidth="0.5"
          />
        ))}
        
        {/* Label area / Album art clip */}
        <clipPath id={`labelClip-${deck}`}>
          <circle cx="100" cy="100" r="35" />
        </clipPath>
        
        {albumArt ? (
          <image 
            href={albumArt}
            x="65" 
            y="65" 
            width="70" 
            height="70"
            clipPath={`url(#labelClip-${deck})`}
            preserveAspectRatio="xMidYMid slice"
          />
        ) : (
          <circle 
            cx="100" 
            cy="100" 
            r="35" 
            fill="#1a1a1a"
          />
        )}
        
        {/* Center spindle */}
        <circle 
          cx="100" 
          cy="100" 
          r="8" 
          fill="#333"
          stroke="#444"
          strokeWidth="2"
        />
        <circle 
          cx="100" 
          cy="100" 
          r="3" 
          fill="#222"
        />
        
        {/* Position marker (the line that shows rotation) */}
        <line 
          x1="100" 
          y1="100" 
          x2="100" 
          y2="25"
          stroke={deckColor}
          strokeWidth="2"
          strokeLinecap="round"
          filter={`url(#glow-${deck})`}
        />
        
        {/* Deck indicator ring - Flashes when playing */}
        <circle 
          cx="100" 
          cy="100" 
          r="96" 
          fill="none"
          stroke={isPlaying || isDragging ? deckColor : deckColorDim}
          strokeWidth={isDragging ? "3" : isPlaying ? "2" : "1"}
          opacity={isPlaying || isDragging ? 1 : 0.5}
          filter={isPlaying || isDragging ? `url(#glow-${deck})` : undefined}
          className={isPlaying && !isDragging ? 'ring-flash' : ''}
        />
        
        {/* Secondary glow ring for playing state */}
        {isPlaying && !isDragging && (
          <circle 
            cx="100" 
            cy="100" 
            r="94" 
            fill="none"
            stroke={deckColor}
            strokeWidth="1"
            opacity="0.5"
            className="ring-flash-secondary"
          />
        )}
      </svg>
      
      {/* Deck label */}
      <div className="jog-deck-label" style={{ color: deckColor }}>
        {deck}
      </div>
      
      {/* Scratch indicator */}
      {isDragging && (
        <div className="scratch-indicator" style={{ color: deckColor }}>
          SCRATCH
        </div>
      )}
    </div>
  );
};

export default JogWheel;
